import { queryOptions, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { withCourseChanges, withLessonChanges, withModuleTitle, type Course } from '../domain/course';
import {
  collapseSpaces,
  emptyLesson,
  LIMITS,
  toCourseBody,
  toLessonBody,
  type AddLessonForm,
  type CourseChanges,
  type CreateCourseForm,
  type LessonChanges,
} from '../domain/schemas';
import { catalogApi, type ListCoursesParams } from '../infrastructure/api';

export const catalogKeys = {
  all: ['catalog'] as const,
  lists: () => [...catalogKeys.all, 'list'] as const,
  list: (params: ListCoursesParams) => [...catalogKeys.lists(), params] as const,
  byId: (id: string) => [...catalogKeys.all, 'id', id] as const,
  bySlug: (slug: string) => [...catalogKeys.all, 'slug', slug] as const,
};

export const coursesQuery = (params: ListCoursesParams) =>
  queryOptions({ queryKey: catalogKeys.list(params), queryFn: () => catalogApi.list(params) });

export const courseByIdQuery = (id: string) =>
  queryOptions({ queryKey: catalogKeys.byId(id), queryFn: () => catalogApi.byId(id), staleTime: 60_000 });

export const courseBySlugQuery = (slug: string) =>
  queryOptions({
    queryKey: catalogKeys.bySlug(slug),
    queryFn: () => catalogApi.bySlug(slug),
    staleTime: 60_000,
  });

/**
 * Toda escrita no catálogo devolve o curso inteiro (`CourseOut`): grava
 * direto no cache das duas chaves de detalhe e invalida as listagens.
 */
function storeCourse(qc: QueryClient, course: Course) {
  qc.setQueryData(catalogKeys.byId(course.id), course);
  qc.setQueryData(catalogKeys.bySlug(course.slug), course);
  void qc.invalidateQueries({ queryKey: catalogKeys.lists() });
}

function useCourseMutation<V>(fn: (vars: V) => Promise<Course>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: (course) => storeCourse(qc, course) });
}

/**
 * Edição no lugar: o cache do curso muda antes da resposta (a tela não
 * espera a rede) e volta ao que era se a API recusar. A resposta, quando
 * chega, é a verdade — substitui a previsão.
 */
function useOptimisticCourseMutation<V>(
  courseId: string,
  fn: (vars: V) => Promise<Course>,
  predict: (course: Course, vars: V) => Course,
) {
  const qc = useQueryClient();
  const key = catalogKeys.byId(courseId);
  return useMutation({
    mutationFn: fn,
    onMutate: async (vars: V) => {
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<Course>(key);
      if (before) qc.setQueryData(key, predict(before, vars));
      return { before };
    },
    onError: (_e, _vars, context) => {
      if (context?.before) qc.setQueryData(key, context.before);
    },
    onSuccess: (course) => storeCourse(qc, course),
  });
}

/** Título, descrição, tags e autor(a) — um campo por vez, ou vários. */
export function useUpdateCourse(courseId: string) {
  return useOptimisticCourseMutation(
    courseId,
    (changes: CourseChanges) => catalogApi.update(courseId, changes),
    withCourseChanges,
  );
}

export function useRenameModule(courseId: string) {
  return useOptimisticCourseMutation(
    courseId,
    ({ moduleId, title }: { moduleId: string; title: string }) =>
      catalogApi.renameModule(courseId, moduleId, title),
    (course, { moduleId, title }) => withModuleTitle(course, moduleId, title),
  );
}

export function useUpdateLesson(courseId: string) {
  return useOptimisticCourseMutation(
    courseId,
    ({ lessonId, changes }: { lessonId: string; changes: LessonChanges }) =>
      catalogApi.updateLesson(courseId, lessonId, changes),
    (course, { lessonId, changes }) => withLessonChanges(course, lessonId, changes),
  );
}

/** Excluir não é otimista: passa por confirmação e espera a API (ela pode recusar). */
export function useDeleteLesson(courseId: string) {
  return useCourseMutation((lessonId: string) => catalogApi.deleteLesson(courseId, lessonId));
}

export function useDeleteModule(courseId: string) {
  return useCourseMutation((moduleId: string) => catalogApi.deleteModule(courseId, moduleId));
}

export function useCreateCourse() {
  return useCourseMutation((form: CreateCourseForm) => catalogApi.create(toCourseBody(form)));
}

export function useAddModule(courseId: string) {
  return useCourseMutation((title: string) => catalogApi.addModule(courseId, collapseSpaces(title)));
}

export function useAddLesson(courseId: string) {
  return useCourseMutation(
    ({ moduleId, form, seconds }: { moduleId: string; form: AddLessonForm; seconds?: number | null }) =>
      catalogApi.addLesson(courseId, moduleId, toLessonBody(form, seconds)),
  );
}

/**
 * Cria uma aula de vídeo e devolve o id dela — para quem cria várias em
 * sequência (envio em lote), uma de cada vez. A aula nova é a que não
 * existia antes no módulo (a API sempre acrescenta no fim).
 */
export function useCreateVideoLesson(courseId: string) {
  const qc = useQueryClient();
  return useCallback(
    async (moduleId: string, title: string, seconds: number | null): Promise<string> => {
      const before = qc.getQueryData<Course>(catalogKeys.byId(courseId));
      const known = new Set(before?.modules.find((m) => m.id === moduleId)?.lessons.map((l) => l.id));
      const body = toLessonBody({ ...emptyLesson(), title: title.slice(0, LIMITS.title) }, seconds);
      const course = await catalogApi.addLesson(courseId, moduleId, body);
      storeCourse(qc, course);
      const created = course.modules
        .find((m) => m.id === moduleId)
        ?.lessons.filter((l) => !known.has(l.id))
        .at(-1);
      if (!created) throw new Error('A aula não foi criada. Recarregue a página.');
      return created.id;
    },
    [qc, courseId],
  );
}

/** Vincula o vídeo recém-enviado à aula (antes do transcode terminar). */
export function useAttachVideo(courseId: string) {
  return useCourseMutation(({ lessonId, assetId }: { lessonId: string; assetId: string }) =>
    catalogApi.attachVideo(courseId, lessonId, assetId),
  );
}

export function useReorderLessons(courseId: string) {
  return useCourseMutation(({ moduleId, ids }: { moduleId: string; ids: string[] }) =>
    catalogApi.reorderLessons(courseId, moduleId, ids),
  );
}

export function usePublishCourse(courseId: string) {
  return useCourseMutation(() => catalogApi.publish(courseId));
}

export function useArchiveCourse(courseId: string) {
  return useCourseMutation(() => catalogApi.archive(courseId));
}
